import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import BaseDropdown from "@/components_v2/FormFields/Dropdown/BaseDropdown";
import BasePagination from "@/components_v2/Pagination/BasePagination";
import BaseSkeleton from "@/components_v2/Skeleton/BaseSkeleton";
import BaseSkeletonGroup from "@/components_v2/Skeleton/BaseSkeletonGroup";
import { SKELETON_VARIANTS } from "@/components_v2/Skeleton/constants";
import { useGetModerationCasesQuery } from "@/redux/service";
import { isPaginatedResponse } from "@/redux/typeGuards";
import { useCertificationWeekByTopic } from "@/domains/certifications/hooks/useCertificationWeekByTopic";
import CaseCard from "@/features/community/moderation/components/CaseCard";
import ModerationFilters from "@/features/community/moderation/components/ModerationFilters";
import {
  CASES_PAGE_SIZE,
  CASE_ORDERINGS,
  CASE_SKELETON_CARDS,
  DEFAULT_FILTERS,
  SORT_KEYS,
  SORT_OPTIONS,
  STATUS_STATES,
} from "@/features/community/moderation/constants";
import { getCertificationWeek } from "@/features/community/moderation/utils";
import {
  ModerationFiltersState,
  SortOption,
} from "@/features/community/moderation/types";

import "./styles.css";

const CommunityModeration = () => {
  const { t } = useTranslation();
  const sortId = useId();
  const [filters, setFilters] =
    useState<ModerationFiltersState>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const weekByTopic = useCertificationWeekByTopic();

  const { data, error, isLoading, isFetching, isError } =
    useGetModerationCasesQuery({
      states: STATUS_STATES[filters.status],
      author: filters.author ?? undefined,
      ordering:
        filters.sort === SORT_KEYS.OLDEST
          ? CASE_ORDERINGS.OLDEST_FIRST
          : CASE_ORDERINGS.NEWEST_FIRST,
      page,
      pageSize: CASES_PAGE_SIZE,
    });
  const cases = data ? (isPaginatedResponse(data) ? data.results : data) : [];
  const total = data && isPaginatedResponse(data) ? data.count : cases.length;

  const isPastLastPage =
    page > 1 &&
    error !== undefined &&
    "status" in error &&
    error.status === 404;
  useEffect(() => {
    if (isPastLastPage) {
      setPage((current) => current - 1);
    }
  }, [isPastLastPage]);

  const handleFiltersChange = (next: ModerationFiltersState) => {
    setFilters(next);
    setPage(1);
  };
  const resetFilters = () => handleFiltersChange(DEFAULT_FILTERS);
  const hasFilters =
    filters.status !== DEFAULT_FILTERS.status || filters.author !== null;

  let content;
  if (isLoading) {
    content = (
      <BaseSkeletonGroup
        label={t("labels.loading")}
        className="flex flex-col gap-300"
      >
        {Array.from({ length: CASE_SKELETON_CARDS }, (_, index) => (
          <div
            key={index}
            className="moderation-card moderation-card-body flex flex-col gap-300"
          >
            <div className="flex items-center gap-200">
              <BaseSkeleton
                variant={SKELETON_VARIANTS.CIRCLE}
                width="var(--spacing-500)"
                height="var(--spacing-500)"
              />

              <BaseSkeleton width="10rem" />
            </div>

            <BaseSkeleton variant={SKELETON_VARIANTS.TEXT} lines={3} />
          </div>
        ))}
      </BaseSkeletonGroup>
    );
  } else if (isError && !isPastLastPage) {
    content = (
      <p className="moderation-empty body-m-regular" role="alert">
        {t("moderation.page.error")}
      </p>
    );
  } else if (cases.length === 0) {
    content = (
      <div className="moderation-empty flex flex-col items-center gap-100">
        <span className="body-m-medium">
          {t(
            hasFilters
              ? "moderation.page.noMatchTitle"
              : "moderation.page.emptyTitle",
          )}
        </span>

        {hasFilters ? (
          <button
            type="button"
            className="moderation-link-button body-s-medium"
            onClick={resetFilters}
          >
            {t("moderation.page.resetFilters")}
          </button>
        ) : (
          <span className="body-s-regular">
            {t("moderation.page.emptyBody")}
          </span>
        )}
      </div>
    );
  } else {
    content = (
      <div
        className={`flex flex-col gap-300 ${isFetching ? "moderation-refreshing" : ""}`}
        aria-busy={isFetching}
      >
        {cases.map((moderationCase) => (
          <CaseCard
            key={moderationCase.id}
            moderationCase={moderationCase}
            certificationWeek={getCertificationWeek(
              moderationCase,
              weekByTopic,
            )}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="moderation-page flex flex-wrap gap-600">
      <div className="moderation-main flex flex-col gap-400">
        <header className="flex flex-col gap-100">
          <h1 className="moderation-heading header-l-medium">
            {t("moderation.page.title")}
          </h1>

          <p className="moderation-muted body-m-regular">
            {t("moderation.page.description")}
          </p>
        </header>

        <div className="moderation-result-row flex flex-wrap items-center justify-between gap-300">
          <span className="body-m-medium" aria-live="polite">
            {t(`moderation.page.count.${filters.status}`, { count: total })}
          </span>

          <div className="flex items-center gap-200">
            <label htmlFor={sortId} className="body-s-medium">
              {t("moderation.page.sortBy")}
            </label>

            <BaseDropdown
              id={sortId}
              className="moderation-sort"
              value={filters.sort}
              options={SORT_OPTIONS.map((sort) => ({
                value: sort,
                label: t(`moderation.page.sort.${sort}`),
              }))}
              onChange={(sort: SortOption) =>
                handleFiltersChange({ ...filters, sort })
              }
            />
          </div>
        </div>

        {content}

        {total > CASES_PAGE_SIZE && (
          <div className="flex justify-end">
            <BasePagination
              current={page}
              pageSize={CASES_PAGE_SIZE}
              total={total}
              onChange={setPage}
              showSizeChanger={false}
            />
          </div>
        )}
      </div>

      <ModerationFilters filters={filters} onChange={handleFiltersChange} />
    </div>
  );
};

export default CommunityModeration;
